import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Row, Col, List, Avatar, Badge, Spin, Alert, Button } from 'antd';
import { ClockCircleOutlined, EnvironmentOutlined, UserOutlined, QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';

dayjs.locale('vi');

const { Title, Text } = Typography;

interface AttendanceSession {
  SessionID: number;
  SessionDate: string;
  SessionTime: string;
  Location: string;
  StartTime: string;
  EndTime: string;
  Status: string;
  TotalPresent: number;
  OpenedAt: string;
  QrData?: string;
  QrExpiry?: string;
}

interface Attendee {
  STT: number;
  Username: string;
  FullName: string;
  RoleName: string;
  Department: string;
  CheckInTime: string;
}

interface TVDisplayProps {
  location?: 'hospital' | 'school' | 'all';
}

const DailyAttendanceTVDisplay: React.FC<TVDisplayProps> = ({ location = 'all' }) => {
  const [sessions, setSessions] = useState<AttendanceSession[]>([]); // Multiple sessions
  const [currentSessionIndex, setCurrentSessionIndex] = useState(0); // For rotation
  const [session, setSession] = useState<AttendanceSession | null>(null); // Current displayed session
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [currentTime, setCurrentTime] = useState(dayjs());
  
  const qrRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const sessionPollInterval = useRef<NodeJS.Timeout | null>(null);
  const sessionRotationInterval = useRef<NodeJS.Timeout | null>(null);
  const clockInterval = useRef<NodeJS.Timeout | null>(null);

  // Auto-detect location from URL if not provided
  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname.includes('/hospital')) {
      // Force hospital location
      window.history.replaceState(null, '', '/daily-attendance/tv/hospital');
    } else if (pathname.includes('/school')) {
      // Force school location
      window.history.replaceState(null, '', '/daily-attendance/tv/school');
    }
  }, []);

  // Update clock every second
  useEffect(() => {
    clockInterval.current = setInterval(() => {
      setCurrentTime(dayjs());
    }, 1000);

    return () => {
      if (clockInterval.current) {
        clearInterval(clockInterval.current);
      }
    };
  }, []);

  // Poll for active sessions every 5 seconds (get ALL active sessions)
  useEffect(() => {
    const fetchActiveSessions = async () => {
      try {
        // Get all sessions for current day with status = 'open'
        const token = localStorage.getItem('admin_token') || localStorage.getItem('quanly_truonghoc_token');
        
        const response = await fetch('/api/v1/daily-attendance/sessions?status=open', {
          headers: token ? {
            'Authorization': `Bearer ${token}`,
          } : {}
        });
        const result = await response.json();
        console.log('[TV Display] All sessions:', result);
        
        if (result.success && result.data && result.data.length > 0) {
          // Filter by location if specified
          let filteredSessions = result.data;
          if (location && location !== 'all') {
            filteredSessions = result.data.filter((s: AttendanceSession) => s.Location === location);
          }
          
          console.log('[TV Display] Filtered sessions:', filteredSessions);
          setSessions(filteredSessions);
          setError('');
        } else {
          console.log('[TV Display] No sessions found');
          setSessions([]);
          setError('Không có phiên điểm danh nào đang mở');
        }
      } catch (err) {
        console.error('[TV Display] Error fetching sessions:', err);
        setError('Lỗi kết nối đến server');
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchActiveSessions();

    // Poll every 5 seconds
    sessionPollInterval.current = setInterval(fetchActiveSessions, 5000);

    return () => {
      if (sessionPollInterval.current) {
        clearInterval(sessionPollInterval.current);
      }
    };
  }, [location]);

  // Rotate between multiple sessions every 15 seconds
  useEffect(() => {
    if (sessions.length === 0) {
      setSession(null);
      return;
    }

    // Set current session
    const currentSession = sessions[currentSessionIndex];
    setSession(currentSession);
    
    // Fetch attendees
    if (currentSession?.SessionID) {
      fetchAttendees(currentSession.SessionID);
    }

    // If multiple sessions, rotate every 15 seconds
    if (sessions.length > 1) {
      sessionRotationInterval.current = setInterval(() => {
        setCurrentSessionIndex((prevIndex) => (prevIndex + 1) % sessions.length);
      }, 15000);
      console.log(`[TV Display] Rotating ${sessions.length} sessions every 15s`);
    }

    return () => {
      if (sessionRotationInterval.current) {
        clearInterval(sessionRotationInterval.current);
      }
    };
  }, [sessions, currentSessionIndex]);

  // Generate QR code from session QrData and refresh every 10 seconds
  useEffect(() => {
    const refreshQRCode = async () => {
      if (!session || !session.SessionID) {
        setQrCodeDataUrl('');
        return;
      }

      try {
        // ✅ FIX: Gọi đúng endpoint /qr-token thay vì /generate-qr
        const token = localStorage.getItem('admin_token') || localStorage.getItem('quanly_truonghoc_token');
        
        const headers: any = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`/api/v1/daily-attendance/${session.SessionID}/qr-token`, {
          method: 'POST',
          headers
        });
        
        const result = await response.json();
        console.log('[QR Refresh] API Response:', result);
        
        if (result.success && result.data?.token) {
          // Generate QR code from new token
          const qrDataUrl = await QRCode.toDataURL(result.data.token, {
            width: 300,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
          setQrCodeDataUrl(qrDataUrl);
          console.log('[QR Refresh] QR code refreshed successfully');
        } else {
          console.error('[QR Refresh] Failed to get token:', result);
          setQrCodeDataUrl('');
        }
      } catch (err) {
        console.error('[QR Refresh] Error:', err);
        setQrCodeDataUrl('');
      }
    };

    if (session) {
      // Generate immediately when session changes
      refreshQRCode();
      
      // Refresh every 10 seconds
      qrRefreshInterval.current = setInterval(refreshQRCode, 10000);
      console.log('[QR Refresh] Started refresh interval (10s)');
    }

    return () => {
      if (qrRefreshInterval.current) {
        clearInterval(qrRefreshInterval.current);
        console.log('[QR Refresh] Cleared refresh interval');
      }
    };
  }, [session]);

  const fetchAttendees = async (sessionId: number) => {
    try {
      const response = await fetch(`/api/v1/daily-attendance/${sessionId}/attendees`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'public'}`,
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setAttendees(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching attendees:', err);
    }
  };

  const getLocationName = (loc: string) => {
    switch (loc) {
      case 'hospital': return 'BỆNH VIỆN';
      case 'school': return 'TRƯỜNG';
      default: return 'TẤT CẢ ĐỊA ĐIỂM';
    }
  };

  const getSessionTimeName = (time: string) => {
    switch (time) {
      case 'morning_start': return 'Đầu giờ sáng';
      case 'morning_end': return 'Cuối giờ sáng';
      case 'afternoon_start': return 'Đầu giờ chiều';
      case 'afternoon_end': return 'Cuối giờ chiều';
      default: return time;
    }
  };

  const formatTime = (timeStr: string) => {
    return dayjs(timeStr, 'HH:mm:ss').format('HH:mm');
  };

  // Auto fullscreen on load
  useEffect(() => {
    const requestFullscreen = () => {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(console.error);
      }
    };

    // Try fullscreen after a short delay
    const timer = setTimeout(requestFullscreen, 1000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <Spin size="large" />
        <Text style={{ color: 'white', marginLeft: 16 }}>Đang tải...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        flexDirection: 'column'
      }}>
        <Alert
          message="Không có phiên điểm danh"
          description={error}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Button 
          type="primary" 
          icon={<ReloadOutlined />}
          onClick={() => window.location.reload()}
        >
          Làm mới
        </Button>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        flexDirection: 'column'
      }}>
        <QrcodeOutlined style={{ fontSize: 64, marginBottom: 16 }} />
        <Title level={2} style={{ color: 'white', textAlign: 'center' }}>
          ĐIỂM DANH CÁN BỘ
        </Title>
        <Text style={{ color: 'white', fontSize: 18 }}>
          {getLocationName(location)}
        </Text>
        <Text style={{ color: 'white', fontSize: 16, marginTop: 8 }}>
          Chưa có phiên điểm danh nào đang mở
        </Text>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 24,
      color: 'white'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <Title level={1} style={{ color: 'white', marginBottom: 8 }}>
          ĐIỂM DANH CÁN BỘ
        </Title>
        <Title level={3} style={{ color: 'white', marginBottom: 16 }}>
          {getLocationName(session.Location)}
        </Title>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClockCircleOutlined />
            <Text style={{ color: 'white', fontSize: 18 }}>
              {currentTime.format('dddd, DD/MM/YYYY HH:mm:ss')}
            </Text>
          </div>
        </div>
        
        {/* Session indicator - show if multiple sessions */}
        {sessions.length > 1 && (
          <div style={{ 
            marginTop: 16, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 8
          }}>
            <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 14 }}>
              Đang hiển thị phiên {currentSessionIndex + 1}/{sessions.length}
            </Text>
            <div style={{ display: 'flex', gap: 6 }}>
              {sessions.map((_, index) => (
                <div 
                  key={index}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index === currentSessionIndex ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                    transition: 'all 0.3s'
                  }}
                />
              ))}
            </div>
            <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12 }}>
              (Tự động chuyển mỗi 15 giây)
            </Text>
          </div>
        )}
      </div>

      <Row gutter={24} style={{ minHeight: '70vh' }}>
        {/* QR Code Section */}
        <Col span={10}>
          <Card 
            style={{ 
              height: '100%', 
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16
            }}
            bodyStyle={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              padding: 24
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={3} style={{ color: '#1890ff', marginBottom: 8 }}>
                {getSessionTimeName(session.SessionTime)}
              </Title>
              <Text style={{ fontSize: 16, color: '#666' }}>
                {formatTime(session.StartTime)} - {formatTime(session.EndTime)}
              </Text>
            </div>

            {qrCodeDataUrl ? (
              <div style={{ textAlign: 'center' }}>
                <img 
                  src={qrCodeDataUrl} 
                  alt="QR Code" 
                  style={{ 
                    maxWidth: 300, 
                    maxHeight: 300,
                    border: '2px solid #1890ff',
                    borderRadius: 8
                  }} 
                />
                <div style={{ marginTop: 16, color: '#666' }}>
                  <Text>Quét mã QR để điểm danh</Text>
                  <br />
                  <Text type="secondary">Tự động đổi mỗi 10 giây</Text>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#666' }}>
                  <Text>Đang tạo mã QR...</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>

        {/* Attendees List Section */}
        <Col span={14}>
          <Card 
            style={{ 
              height: '100%', 
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 16
            }}
            bodyStyle={{ height: '100%', padding: 24 }}
          >
            <div style={{ marginBottom: 24 }}>
              <Title level={3} style={{ color: '#1890ff', marginBottom: 8 }}>
                Danh sách điểm danh
              </Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Badge 
                  count={session.TotalPresent} 
                  style={{ backgroundColor: '#52c41a' }}
                />
                <Text style={{ fontSize: 16 }}>
                  Đã điểm danh: <strong>{session.TotalPresent}</strong> người
                </Text>
              </div>
            </div>

            <div style={{ 
              height: 'calc(100% - 120px)', 
              overflowY: 'auto',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 16
            }}>
              {attendees.length > 0 ? (
                <List
                  dataSource={attendees}
                  renderItem={(attendee, index) => (
                    <List.Item
                      key={attendee.STT}
                      style={{ 
                        padding: '12px 0',
                        borderBottom: index < attendees.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar 
                            style={{ backgroundColor: '#1890ff' }}
                            icon={<UserOutlined />}
                          />
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Text strong>{attendee.FullName}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {attendee.RoleName} - {attendee.Department}
                              </Text>
                            </div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {dayjs(attendee.CheckInTime).format('HH:mm:ss')}
                            </Text>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 0',
                  color: '#999'
                }}>
                  <UserOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                  <div>Chưa có ai điểm danh</div>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Footer */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: 24,
        color: 'rgba(255, 255, 255, 0.8)'
      }}>
        <Text>
          Hệ thống điểm danh tự động - Cập nhật mỗi 5 giây
        </Text>
      </div>
    </div>
  );
};

export default DailyAttendanceTVDisplay;
